import { AssetGroup } from '@play-co/timestep-core';
import { View, ImageScaleView } from '@play-co/timestep-core/ui';
import Scroll from 'src/game/components/shared/Scroll';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';
import { Feature } from 'src/replicant/analytics';
import type { CardId } from 'src/replicant/cards/cards.ruleset';
import { chunkArray } from 'src/replicant/utils/array';
import { Card } from '../components/Card';

const ITEM_PANEL_IMAGE = 'assets/ui/shared/frames/scroller_panel_blue.png';

export type PopupCardPickerCard = {
  id: CardId;
  count: number;
};

export type PopupCardPickerOpenArgs = {
  title: string;
  cards: PopupCardPickerCard[];
  buttonText?: string;
  /** Optional root stacking order for callers that open the picker over debug UI. */
  zIndex?: number;
};

type PopupCardPickerClose = (card?: PopupCardPickerCard) => void;

type CardPickerLine = PopupCardPickerCard[];

const ROW_WIDTH = 535;
const ROW_HEIGHT = 280;
const ITEM_WIDTH = 160;
const ITEM_HEIGHT = 280;
const ITEM_GAP = 10;
const CARD_SCALE = 0.7;
const CARD_WIDTH = 184 * CARD_SCALE;
const BUTTON_WIDTH = 125;

export default class PopupCardPicker extends PopupBasic<PopupCardPickerClose> {
  private scroll: Scroll<CardPickerLine>;
  private buttonText = '';

  constructor(opts: { superview: View; close: PopupCardPickerClose }) {
    super({
      superview: opts.superview,
      close: opts.close,
      zIndex: 10000,
      width: 582,
      height: 720,
      darkerBg: true,
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.SEND,
      },
    });

    this.scroll = new Scroll<CardPickerLine>({
      superview: this.box,
      createItem: this.createItem.bind(this),
      showBg: true,
      firstItemOffset: 5,
      lastItemOffset: 10,
      margin: 7,
      rect: {
        x: 25,
        y: 80,
        width: 530,
        height: 600,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs(['assets/ui/cards/common']),
      ITEM_PANEL_IMAGE,
      'assets/ui/popups/scroller_panel.png',
      uiConfig.buttons.secondary.image,
      uiConfig.buttons.secondary.imagePressed,
    ]);
  }

  private createItem(superview: View, _: number, cards: CardPickerLine) {
    const view = new View({
      tag: 'card-picker-row',
      superview,
      width: ROW_WIDTH,
      height: ROW_HEIGHT,
    });

    cards.forEach((card, index) => {
      if (!card) return;
      this.createCardItem(view, card, index);
    });

    return view;
  }

  private createCardItem(
    superview: View,
    card: PopupCardPickerCard,
    index: number,
  ) {
    const startX = 15;
    const view = new View({
      tag: 'card-picker-item',
      superview,
      x: startX + index * (ITEM_WIDTH + ITEM_GAP),
      width: ITEM_WIDTH,
      height: ITEM_HEIGHT,
    });

    new ImageScaleView({
      tag: 'frame',
      superview: view,
      image: ITEM_PANEL_IMAGE,
      scaleMethod: '9slice',
      width: ITEM_WIDTH,
      height: ITEM_HEIGHT,
      sourceSlices: {
        vertical: { top: 35, bottom: 35 },
        horizontal: { left: 35, right: 35 },
      },
    });

    const cardView = new Card({
      superview: view,
      x: (ITEM_WIDTH - CARD_WIDTH) * 0.5,
      y: 15,
      scale: CARD_SCALE,
    });
    cardView.setState(card);
    cardView.onClick = () => this.selectCard(card);

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: view,
      font: 'Body',
      fontSize: 28,
      labelOffsetY: -3,
      x: (ITEM_WIDTH - BUTTON_WIDTH) * 0.5,
      y: 195,
      width: BUTTON_WIDTH,
      height: 65,
      lineSpacing: -8,
      localeText: () => this.buttonText,
      onClick: () => this.selectCard(card),
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.SEND,
      },
    });

    return view;
  }

  private async selectCard(card: PopupCardPickerCard) {
    sounds.sfx.play('ok');
    this.opts.close(card);
  }

  /**
   * Populate and display the card picker.
   *
   * @param opts Picker title, cards, button label, and optional stacking order.
   */
  public init(opts: PopupCardPickerOpenArgs) {
    const { title, cards, buttonText, zIndex } = opts;

    // Apply any caller-specific stacking order before the popup is attached.
    if (zIndex !== undefined) {
      this.root.updateOpts({ zIndex });
    }

    // Initialize the shared popup frame and title.
    super.init({
      title,
    });

    // Populate the selectable card rows with the requested action label.
    this.buttonText = buttonText ?? i18n('cards.cardTrade.propose');
    this.scroll.setItems(chunkArray(cards, 3));
  }
}
